import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { ArrowLeft } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) { navigate("/dashboard", { replace: true }); return null; }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F0F0F0] flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#050505] border-r border-[#1E1E1E] p-12 flex-col justify-between">
        <div>
          <Link to="/" className="font-heading text-xl font-black tracking-tighter uppercase" data-testid="login-logo">
            NRD<span className="text-[#00FF41]">.</span>
          </Link>
        </div>
        <div>
          <h2 className="font-heading text-4xl font-black tracking-tighter uppercase mb-4">Commerce<br />Automation<br /><span className="text-[#00FF41]">Suite</span></h2>
          <p className="text-slate-500 text-sm max-w-sm">Four precision-engineered tools to transform your commerce operations.</p>
        </div>
        <p className="text-xs text-slate-700">&copy; 2025 NoRespectDigital</p>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#00FF41] mb-8 transition-colors" data-testid="login-back">
            <ArrowLeft size={14} /> Back
          </Link>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-2" data-testid="login-title">Sign In</h1>
          <p className="text-sm text-slate-500 mb-8">Enter your credentials to access your dashboard.</p>

          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-800 text-sm text-red-400" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">Email</label>
              <input
                type="email" required value={email} onChange={e => setEmail(e.target.value)}
                className="w-full bg-[#111111] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] focus:outline-none focus:border-[#00FF41] transition-colors"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">Password</label>
              <input
                type="password" required value={password} onChange={e => setPassword(e.target.value)}
                className="w-full bg-[#111111] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] focus:outline-none focus:border-[#00FF41] transition-colors"
                data-testid="login-password-input"
              />
            </div>
            <button
              type="submit" disabled={loading}
              className="w-full bg-[#00FF41] text-[#0A0A0A] py-3 text-sm font-bold hover:bg-[#00CC33] transition-colors disabled:opacity-50"
              data-testid="login-submit-button"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <p className="mt-6 text-sm text-slate-500 text-center">
            Don't have an account? <Link to="/register" className="text-[#00FF41] font-medium hover:text-[#00CC33]" data-testid="login-register-link">Create one</Link>
          </p>
          <p className="mt-2 text-sm text-center">
            <Link to="/forgot-password" className="text-slate-500 hover:text-[#00FF41] transition-colors" data-testid="login-forgot-link">Forgot password?</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
